import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Skeleton } from '@mui/material';
import { t } from 'i18next';
import { Trans } from 'react-i18next';

import ActionBar from '../../../../../../../shared/components/v3/ActionBar/ActionBar';
import BdsCircleIndicatorPictogram from '../../../../../../../shared/components/v3/BdsCircleIndicator/BdsCircleIndicatorPictogram/BdsCircleIndicatorPictogram';
import StatusCard from '../../../../../../../shared/components/v3/Cards/StatusCard/StatusCard';
import StyledTable from '../../../../../../../shared/components/v3/Table/StyledTable/StyledTable';
import TablePaginationV3 from '../../../../../../../shared/components/v3/TablePagination/TablePagination';
import Toast from '../../../../../../../shared/components/v3/Toast/Toast';
import {
  DEFAULT_PAGINATOR_OPTIONS_V3,
  DEFAULT_PAGINATOR_STATE_V3
} from '../../../../../../../shared/constants/constants';
import { CHECK_ICON_V2 } from '../../../../../../../shared/constants/images';
import { COUNTRIES_WITH_DEPENDENTS_DOC_TYPE } from '../../../../../../../shared/constants/manageCollaborators';
import { MANAGE_COLLABORATORS_SECTION } from '../../../../../../../shared/constants/tagManager';
import { DELETE_COLLABORATOR_SUCCESS_TOAST } from '../../../../../../../shared/constants/toast';
import { CompanyContext } from '../../../../../../../shared/context/company/companyContext';
import { ADD_DEPENDENTS_ERROR_TABLE_COLUMNS } from '../../../../../../../shared/handlers/tableColumns';
import type { IDependentsWithErrors } from '../../../../../../../shared/interfaces/manageCollaborators';
import type { ISelectedMap } from '../../../../../../../shared/interfaces/tables';
import { TRANSLATION_MANAGE_COLLABORATORS } from '../../../../../../../shared/translations/manageCollaborators';
import { isEmptyObject } from '../../../../../../../shared/utils/validation/validation';
import { hasErrors } from '../../utils/hasErrorField';

import './AddDependentsByFileTable.scss';

interface IAddDependentsByFileTable {
  dependentsWithError: IDependentsWithErrors[];
  setDependentToUpload: (dependents: IDependentsWithErrors[]) => void;
  setReadyToUpload: (ready: boolean) => void;
}

const AddDependentsByFileTable = ({
  dependentsWithError,
  setDependentToUpload,
  setReadyToUpload
}: IAddDependentsByFileTable) => {
  const [dependentsReference, setDependentsReference] = useState<IDependentsWithErrors[]>();
  const [dependentsUpdated, setDependentsUpdated] = useState<IDependentsWithErrors[]>([]);
  const [dependentsDeleted, setDependentsDeleted] = useState<IDependentsWithErrors[]>([]);
  const [lastUpdatedDependent, setLastUpdatedDependent] = useState<IDependentsWithErrors | null>(null);
  const [dependentsJustDeleted, setDependentsJustDeleted] = useState<boolean>(false);
  const [currentPageData, setCurrentPageData] = useState<IDependentsWithErrors[]>([]);

  const [dependentsWithErrors, setDependentsWithErrors] = useState<number>(0);
  const [paginatorState, setPaginatorState] = useState<number>(DEFAULT_PAGINATOR_STATE_V3);
  const [actualPage, setActualPage] = useState<number>(0);
  const prevDependentsWithErrorsRef = useRef<number>(0);

  const [selectedMap, setSelectedMap] = useState<ISelectedMap>({});
  const [allSelected, setAllSelected] = useState(false);
  const [totalSelected, setTotalSelected] = useState(0);
  const [openToast, setOpenToast] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingPagination, setloadingPagination] = useState(false);

  const tableRef = React.createRef<HTMLDivElement>();
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const { company } = useContext(CompanyContext);

  const columns = () => {
    const shouldShowDocType = COUNTRIES_WITH_DEPENDENTS_DOC_TYPE.includes(company.countryCode.toUpperCase());
    const generalColumn = shouldShowDocType
      ? ADD_DEPENDENTS_ERROR_TABLE_COLUMNS
      : ADD_DEPENDENTS_ERROR_TABLE_COLUMNS.filter((column) => column.key !== 'dependentDocType');

    return generalColumn;
  };

  const insertDash = useCallback((value: string, index: number) => {
    return `${value}/${index}`;
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;

    if (target.tagName === 'INPUT' || target.isContentEditable) {
      return;
    }

    if (!tableRef.current) return;

    setIsDragging(true);
    setStartX(e.pageX - tableRef.current.offsetLeft);
    setScrollLeft(tableRef.current.scrollLeft);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !tableRef.current) return;
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.isContentEditable) {
      setIsDragging(false);
      return;
    }
    e.preventDefault();
    const x = e.pageX - tableRef.current.offsetLeft;
    const walk = startX - x;
    tableRef.current.scrollLeft = scrollLeft + walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    setLoading(true);

    const timerId = setTimeout(() => {
      setDependentsUpdated(dependentsWithError);
      setDependentsReference(dependentsWithError);
      setLoading(false);
    }, 10);

    return () => {
      clearTimeout(timerId);
    };
  }, [dependentsWithError]);

  const totalDependents = useMemo(() => {
    return dependentsUpdated ? dependentsUpdated.length : 0;
  }, [dependentsUpdated]);

  const updateDependentsToUpload = useCallback(
    (dependents: IDependentsWithErrors[]) => {
      const dependentsWithoutErrors = dependents.filter((dependent) => !hasErrors(dependent));
      setDependentToUpload(dependentsWithoutErrors);
    },
    [setDependentToUpload]
  );

  useEffect(() => {
    if (!dependentsUpdated) return;

    const newDependentsWithErrors = dependentsUpdated.filter(hasErrors);
    const newDependentsWithErrorsCount = newDependentsWithErrors.length;
    setDependentsWithErrors(newDependentsWithErrorsCount);

    if (prevDependentsWithErrorsRef.current !== newDependentsWithErrorsCount) {
      setReadyToUpload(newDependentsWithErrorsCount === 0);
      prevDependentsWithErrorsRef.current = newDependentsWithErrorsCount;
    }

    if (lastUpdatedDependent && !hasErrors(lastUpdatedDependent)) {
      updateDependentsToUpload(dependentsUpdated);
      setLastUpdatedDependent(null);
    } else if (dependentsJustDeleted) {
      updateDependentsToUpload(dependentsUpdated);
      setDependentsJustDeleted(false);
    }
  }, [dependentsUpdated, lastUpdatedDependent, dependentsJustDeleted, updateDependentsToUpload, setReadyToUpload]);

  const handleOnChangeField = (field: string, updatedDependent: IDependentsWithErrors) => {
    setDependentsUpdated((prev) =>
      prev.map((dependent, index) => (index === Number(field.split('/')[1]) ? updatedDependent : dependent))
    );
    setLastUpdatedDependent(updatedDependent);
  };

  const onCheckRow = useCallback((key: string) => {
    setSelectedMap((prevMap) => {
      const newMap = { ...prevMap };
      if (newMap[String(key)]) {
        delete newMap[String(key)];
      } else {
        newMap[String(key)] = true;
      }
      return newMap;
    });
  }, []);

  const onCheckAll = useCallback(() => {
    if (allSelected) {
      setSelectedMap({});
    } else {
      const newMap = dependentsUpdated?.reduce((map: ISelectedMap, obj, index) => {
        const key = insertDash(obj.dependentDocValue.value, index);
        map[key] = true;
        return map;
      }, {});
      setSelectedMap(newMap ?? {});
    }
    setAllSelected(!allSelected);
  }, [allSelected, dependentsUpdated, insertDash]);

  useEffect(() => {
    const selected = Object.values(selectedMap).filter(Boolean).length;
    setTotalSelected(selected);
  }, [selectedMap]);

  const handleDeleteRow = useCallback(() => {
    if (dependentsUpdated) {
      const selectedDependents = Object.keys(selectedMap).filter((key) => selectedMap[key]);
      setDependentsUpdated((prev) => {
        const remainingDependents = prev.filter(
          (dependent, index) => !selectedDependents.includes(insertDash(dependent.dependentDocValue.value, index))
        );
        setDependentsDeleted(
          prev.filter((dependent, index) =>
            selectedDependents.includes(insertDash(dependent.dependentDocValue.value, index))
          )
        );
        return remainingDependents;
      });

      setSelectedMap((prevMap) => {
        const newMap = { ...prevMap };
        selectedDependents.forEach((key) => {
          delete newMap[key];
        });
        return newMap;
      });
      setAllSelected(false);
      setOpenToast(true);
      setDependentsJustDeleted(true);
    }
  }, [dependentsUpdated, selectedMap, insertDash]);

  const handleUndoDelete = () => {
    if (!dependentsDeleted.length || !dependentsReference) return;

    setDependentsUpdated((prev) => {
      const updated = prev ? [...prev] : [];
      const restoredDependents = restoreDeletedDependents(updated);
      return restoredDependents;
    });

    setOpenToast(false);
    setDependentsDeleted([]);
    setDependentsJustDeleted(true);
  };

  const restoreDeletedDependents = (updated: IDependentsWithErrors[]) => {
    dependentsDeleted.forEach((deletedDependent) => {
      const index = findDependentIndex(deletedDependent);
      if (index > -1) {
        updated.splice(index, 0, deletedDependent);
      } else {
        updated.unshift(deletedDependent);
      }
    });
    return updated;
  };

  const findDependentIndex = (deletedDependent: IDependentsWithErrors) => {
    if (!dependentsReference) return -1;
    return dependentsReference.findIndex(
      (dependent) => dependent.dependentDocValue.value === deletedDependent.dependentDocValue.value
    );
  };

  const paginateDependents = useCallback(() => {
    const from = actualPage * paginatorState;
    const to = from + paginatorState;
    const pageData = dependentsUpdated?.slice(from, to);

    setCurrentPageData(pageData || []);
    setloadingPagination(false);
  }, [actualPage, paginatorState, dependentsUpdated]);

  const handleOnPageChanged = (page: any) => {
    setActualPage(page);
    setloadingPagination(true);
  };

  const handleOnRowsPerPageChanged = (page: any) => {
    setPaginatorState(page);
    setloadingPagination(true);
  };

  useEffect(() => {
    if (dependentsUpdated) {
      paginateDependents();
    }
  }, [dependentsUpdated, paginateDependents]);

  const handleCloseToast = () => {
    setOpenToast(false);
    setDependentsDeleted([]);
  };

  const closeActionBar = useCallback(() => {
    setSelectedMap({});
    setAllSelected(false);
    setTotalSelected(0);
  }, []);

  const renderToast = () => {
    return (
      <Toast
        description={t(
          TRANSLATION_MANAGE_COLLABORATORS.addDependentsCard.addDependentsByFile.table.toast.deleteSucess,
          { count: dependentsDeleted.length }
        )}
        open={openToast}
        button={DELETE_COLLABORATOR_SUCCESS_TOAST.button}
        buttonText={t(DELETE_COLLABORATOR_SUCCESS_TOAST.buttonText)}
        buttonAction={handleCloseToast}
        handleClose={handleCloseToast}
        secondaryActionData={{
          action: handleUndoDelete,
          text: t(TRANSLATION_MANAGE_COLLABORATORS.addDependentsCard.addDependentsByFile.table.toast.button)
        }}
        icon={DELETE_COLLABORATOR_SUCCESS_TOAST.icon}
      />
    );
  };

  const renderStatusCard = useCallback(() => {
    return (
      <StatusCard
        type="error"
        message={
          <Trans
            i18nKey={TRANSLATION_MANAGE_COLLABORATORS.addDependentsCard.addDependentsByFile.table.statusCard}
            components={{
              b: <b />
            }}
            count={dependentsWithErrors}
            values={{
              rows: dependentsWithErrors
            }}
          />
        }
      />
    );
  }, [dependentsWithErrors]);

  if (dependentsWithErrors < 1 && !loading) {
    return (
      <div className="adby-without-errors flex flex-col justify-start items-start">
        <BdsCircleIndicatorPictogram
          circleIndicatorSize="l"
          className="pictogram"
          pictogram={{
            component: CHECK_ICON_V2
          }}
        />

        <div className="info flex flex-col justify-start items-start">
          <div className="title">
            {t(TRANSLATION_MANAGE_COLLABORATORS.addDependentsCard.addDependentsByFile.table.withoutErrors.title)}
          </div>
          <div className="subtitle">
            {t(TRANSLATION_MANAGE_COLLABORATORS.addDependentsCard.addDependentsByFile.table.withoutErrors.description)}
          </div>
        </div>
        {renderToast()}
      </div>
    );
  }

  return (
    <div className="add-dependents-by-file-container">
      {loading && <Skeleton className="w-full" animation={false} height={70} />}
      {dependentsWithErrors >= 1 && !loading && <div className="info-row w-full">{renderStatusCard()}</div>}
      <div
        className="add-dependents-by-file-table w-full"
        data-actual-page={actualPage}
        data-rows-per-page={paginatorState}
        ref={tableRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseUp}
        onMouseDown={handleMouseDown}
      >
        <StyledTable
          data={currentPageData}
          columns={columns()}
          error={false}
          loading={loading || loadingPagination}
          dataKey="dependentDocValue"
          onChange={handleOnChangeField}
          checkProps={{ onCheckRow, onCheckAll, checkedRows: selectedMap, allSelected }}
          showShadowHeader={false}
        />
        {dependentsUpdated && (
          <div className="paginator-container">
            <TablePaginationV3
              marginTop={40}
              rowsPerPageOptions={DEFAULT_PAGINATOR_OPTIONS_V3}
              rowsPerPage={paginatorState}
              count={totalDependents}
              actualPage={actualPage}
              onPageChange={handleOnPageChanged}
              onRowsPerPageChange={handleOnRowsPerPageChanged}
              sectionPage={MANAGE_COLLABORATORS_SECTION}
            />
          </div>
        )}
        {selectedMap && !isEmptyObject(selectedMap) && (
          <div className="add-dependents-by-file-table-action-bar">
            <ActionBar
              text={t(TRANSLATION_MANAGE_COLLABORATORS.addDependentsCard.addDependentsByFile.table.actionBar.selected, {
                items: totalSelected,
                count: totalSelected
              })}
              deleteTooltip={
                TRANSLATION_MANAGE_COLLABORATORS.addDependentsCard.addDependentsByFile.table.actionBar.delete
              }
              onPressDelete={handleDeleteRow}
              onPressX={closeActionBar}
            />
          </div>
        )}
        {renderToast()}
      </div>
    </div>
  );
};

export default AddDependentsByFileTable;
